import { memo } from "react";
import { Volume2, VolumeX } from "lucide-react";

import { useChatSettingsStore } from "@/features/tutor/store/useChatSettingsStore";
import { useUIStore } from "@/features/tutor/store/useUIStore";
import { useCaptionsUnavailable } from "@/features/tutor/hooks/useCaptionsUnavailable";
import { announceChannelRefusal } from "@/features/tutor/utils/announceChannelRefusal";

const CaptionsGlyph = ({ on }: { on: boolean }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <rect x="2" y="2" width="20" height="20" rx="3.5" fill="currentColor" />
    <path
      d="M10 8.5H7.75A1.75 1.75 0 0 0 6 10.25v3.5a1.75 1.75 0 0 0 1.75 1.75H10M18 8.5h-2.25A1.75 1.75 0 0 0 14 10.25v3.5a1.75 1.75 0 0 0 1.75 1.75H18"
      stroke="#252633"
      strokeWidth="2.4"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    {!on && (
      <path
        d="M4.5 19.5 19.5 4.5"
        stroke="#252633"
        strokeWidth="2.6"
        strokeLinecap="round"
      />
    )}
  </svg>
);

const buttonClass =
  "flex h-11 w-11 items-center justify-center rounded-xl bg-black/50 transition-colors hover:bg-black/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70";

const ChannelToggles = () => {
  const tutorAudioOn = useChatSettingsStore((s) => s.tutorAudioOn);
  const setTutorAudioOn = useChatSettingsStore((s) => s.setTutorAudioOn);
  const setCaptionsOn = useChatSettingsStore((s) => s.setCaptionsOn);
  const captionsOpen = useUIStore((s) => s.captionsOpen);
  const captionsUnavailable = useCaptionsUnavailable();

  return (
    <div
      className="absolute left-2 top-2 z-[1001] flex items-center gap-0"
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        aria-label={tutorAudioOn ? "Mute tutor" : "Unmute tutor"}
        aria-pressed={!tutorAudioOn}
        title={tutorAudioOn ? "Mute tutor" : "Unmute tutor"}
        className={`${buttonClass} text-white`}
        onClick={() => announceChannelRefusal(setTutorAudioOn(!tutorAudioOn))}
      >
        {tutorAudioOn ? <Volume2 size={18} /> : <VolumeX size={18} />}
      </button>

      {!captionsUnavailable && (
        <button
          type="button"
          aria-label={captionsOpen ? "Turn captions off" : "Turn captions on"}
          aria-pressed={captionsOpen}
          title={captionsOpen ? "Captions on" : "Captions off"}
          className={`${buttonClass} ${captionsOpen ? "text-white" : "text-[#7C818C]"}`}
          onClick={() => announceChannelRefusal(setCaptionsOn(!captionsOpen))}
        >
          <CaptionsGlyph on={captionsOpen} />
        </button>
      )}
    </div>
  );
};

export default memo(ChannelToggles);
